import { io } from 'socket.io-client';
import { refreshAccessToken } from '../../../Config/ApiConfig';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.connecting = false;
    this.connectionListeners = [];
    this.errorListeners = [];
    this.eventListeners = {};
    this.resourceCounters = {};
    this.resourceIdCounters = {};
    this.config = {
      serverUrl: '',
      accessToken: '',
    };
    this.isRefreshingToken = false;
    this.reconnectQueue = [];
    this.lastEventDateTime = null;
    this.lastAuthErrorAt = null;
    this.timeOffset = 0; // Difference between server time and client time (server - client)
  }

  /**
   * Updates the server time offset based on a fresh server timestamp.
   * @param {string} serverIsoString - ISO timestamp from server
   */
  updateServerTime(serverIsoString) {
    if (!serverIsoString) return;
    try {
      const serverTime = new Date(serverIsoString).getTime();
      const clientTime = Date.now();
      // Calculate offset: if server is ahead, offset is positive.
      // server = client + offset
      this.timeOffset = serverTime - clientTime;
    } catch (e) {
      console.warn('Failed to parse server time for sync:', e);
    }
  }

  /**
   * Returns the current time corrected by the server offset.
   * @returns {number} Timestamp representing current server time
   */
  getCorrectedNow() {
    return Date.now() + this.timeOffset;
  }

  // Connection methods
  initialize(serverUrl, accessToken) {
    this.config.serverUrl = serverUrl;
    this.config.accessToken = accessToken;
  }

  connect() {
    if (!this.config.serverUrl) return;

    // If already connecting, don't attempt to connect again
    if (this.connecting) {
      return;
    }

    this.connecting = true;
    this._notifyConnectionListeners({ status: 'connecting' });

    this.socket = io(this.config.serverUrl, {
      auth: { access_token: this.config.accessToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      path: '/socket.io',
      transports: ['websocket'],
      upgrade: false,
      forceNew: true,
      secure: true,
      rejectUnauthorized: false,
      withCredentials: true
    });

    this._setupSocketListeners();
  }

  disconnect() {
    if (!this.socket) return;

    // First perform a soft disconnect (tears down the socket connection
    // but preserves in‑memory listeners, counters, and timestamps)
    this.softDisconnect();

    // Then, for a full hard disconnect, reset all state so that no
    // subscriptions, listeners, or counters remain.
    this.lastEventDateTime = null;
    this.resourceCounters = {};
    this.eventListeners = {};
    this.resourceIdCounters = {};
    this.reconnectQueue = [];
    this.isRefreshingToken = false;
    this.connectionListeners = [];
    this.errorListeners = [];
  }

  /**
   * Perform a "soft" disconnect.
   *
   * Used when components unmount or the token changes, but we want to preserve:
   * - existing event listeners
   * - subscription counters
   * - lastEventDateTime (for from_datetime resubscribe)
   * - connection/error listeners
   *
   * On the next connect(), the service will:
   * - re‑establish the low‑level socket connection
   * - call _setupSocketListeners()
   * - call _setupEventListeners()
   * - call _resubscribeAll() to restore subscriptions
   */
  softDisconnect() {
    if (!this.socket) return;

    this.connected = false;
    this.connecting = false;

    // Detach all event handlers for this socket instance,
    // but keep our in‑memory listener and subscription state.
    Object.keys(this.eventListeners).forEach(eventName => {
      this.socket.off(eventName);
    });

    this.socket.disconnect();
    this._notifyConnectionListeners({ status: 'disconnected' });

    // IMPORTANT: Unlike disconnect(), we DO NOT reset:
    // - lastEventDateTime
    // - resourceCounters / resourceIdCounters
    // - eventListeners map
    // - reconnectQueue / isRefreshingToken
    // - connectionListeners / errorListeners
    //
    // We only drop the underlying socket instance so that a future connect()
    // can re‑establish the connection, re‑attach listeners, and resubscribe.
    this.socket = null;
  }

  updateToken(accessToken) {
    this.config.accessToken = accessToken;
    // Always update auth payload on the underlying socket instance
    // so that subsequent reconnect attempts (when disconnected) use
    // the latest token as well.
    if (this.socket) {
      this.socket.auth = { access_token: accessToken };
    }
  }

  // Note: We avoid heuristics on connect errors; refresh only on explicit 401 errors

  // Token refresh handling
  async _handleAuthError() {
    // If already refreshing, add to queue
    if (this.isRefreshingToken) {
      return new Promise((resolve, reject) => {
        this.reconnectQueue.push({ resolve, reject });
      });
    }

    this.isRefreshingToken = true;

    try {
      const newToken = await refreshAccessToken();

      // Update the socket token
      this.updateToken(newToken.replace('Bearer ', ''));

      // Process queue
      this.reconnectQueue.forEach(promise => promise.resolve(newToken));
      this.reconnectQueue = [];

      // If currently disconnected, trigger a reconnect attempt
      if (this.socket && this.socket.disconnected) {
        this.socket.connect();
      }

      return newToken;
    } catch (error) {
      console.error('Failed to refresh token for socket connection:', error);

      // Reject all pending promises
      this.reconnectQueue.forEach(promise => promise.reject(error));
      this.reconnectQueue = [];
      throw error;
    } finally {
      this.isRefreshingToken = false;
    }
  }

  // Event listener methods
  onConnect(listener) {
    this.connectionListeners.push(listener);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(l => l !== listener);
    };
  }

  onError(listener) {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== listener);
    };
  }

  listenToEvent(resource, action, callback) {
    const eventName = `${resource}:${action}`;

    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];

      if (this.socket) {
        this.socket.on(eventName, (data) => {
          this._handleEvent(eventName, data);
        });
      }
    }

    this.eventListeners[eventName].push(callback);

    return () => this.stopListeningToEvent(resource, action, callback);
  }

  stopListeningToEvent(resource, action, callback) {
    const eventName = `${resource}:${action}`;

    if (!this.eventListeners[eventName]) return;

    this.eventListeners[eventName] = this.eventListeners[eventName].filter(cb => cb !== callback);

    if (this.eventListeners[eventName].length === 0) {
      delete this.eventListeners[eventName];

      if (this.socket) {
        this.socket.off(eventName);
      }
    }
  }

  // Resource subscription methods - OPTIMIZED for memory management
  subscribeToResource(resource, resourceIds = null, fromDateTime = null) {
    if (!this.connected) {
      console.warn('Cannot subscribe when socket is not connected');
      return;
    }

    // Removed max subscription limiting to allow full set of IDs

    if (!resourceIds) {
      // Subscribe to the entire resource
      this.resourceCounters[resource] = (this.resourceCounters[resource] || 0) + 1;

      if (this.resourceCounters[resource] === 1) {
        // First subscription to this resource
        this._emitSubscription(resource, null, fromDateTime);
      }
    } else {
      // Subscribe to specific resource IDs
      if (!this.resourceIdCounters[resource]) {
        this.resourceIdCounters[resource] = {};
      }

      const idsToSubscribe = [];



      resourceIds.forEach(id => {
        this.resourceIdCounters[resource][id] = (this.resourceIdCounters[resource][id] || 0) + 1;



        if (this.resourceIdCounters[resource][id] === 1) {
          idsToSubscribe.push(id);

        }
      });

      if (idsToSubscribe.length > 0) {
        this._emitSubscription(resource, idsToSubscribe, fromDateTime);
      }
    }
  }

  /**
   * Updates the subscription time for a resource without modifying reference counters.
   * Useful for syncing the "read cursor" when new events arrive.
   * 
   * @param {string} resource - Resource name
   * @param {Array} resourceIds - Array of resource IDs (optional)
   * @param {string} fromDateTime - New timestamp to sync from
   */
  syncSubscription(resource, resourceIds = null, fromDateTime = null) {
    if (!this.connected || !fromDateTime) return;

    // Update global last event time if the new one is more recent
    if (this.lastEventDateTime) {
      if (new Date(fromDateTime) > new Date(this.lastEventDateTime)) {
        this.lastEventDateTime = fromDateTime;
      }
    } else {
      this.lastEventDateTime = fromDateTime;
    }

    // Unsubscribe first to avoid "Already subscribed" error from backend
    // This allows us to re-subscribe with the new from_datetime to catch up on missed events
    this._emitUnsubscription(resource, resourceIds);

    // Emit subscription to update server-side cursor
    this._emitSubscription(resource, resourceIds, fromDateTime);
  }

  unsubscribeFromResource(resource, resourceIds = null) {
    if (!this.connected) return;

    if (!resourceIds) {
      // Unsubscribe from the entire resource
      if (!this.resourceCounters[resource]) return;

      this.resourceCounters[resource] -= 1;

      if (this.resourceCounters[resource] <= 0) {
        delete this.resourceCounters[resource];
        this._emitUnsubscription(resource);
      }
    } else {
      // Unsubscribe from specific resource IDs
      if (!this.resourceIdCounters[resource]) return;

      const idsToUnsubscribe = [];

      resourceIds.forEach(id => {
        if (!this.resourceIdCounters[resource][id]) return;

        this.resourceIdCounters[resource][id] -= 1;

        if (this.resourceIdCounters[resource][id] <= 0) {
          delete this.resourceIdCounters[resource][id];
          idsToUnsubscribe.push(id);
        }
      });

      if (idsToUnsubscribe.length > 0) {
        this._emitUnsubscription(resource, idsToUnsubscribe);
      }

      // Clean up if no more subscriptions for this resource
      if (Object.keys(this.resourceIdCounters[resource]).length === 0) {
        delete this.resourceIdCounters[resource];
      }
    }
  }

  // Private methods
  _setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connected = true;
      this.connecting = false;

      this._notifyConnectionListeners({ status: 'connected' });

      // Ensure listeners are attached before (re)subscribing to avoid missing replayed events
      this._setupEventListeners();
      this._resubscribeAll();
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      this._notifyConnectionListeners({ status: 'disconnected' });
      // Do NOT call _handleAuthError() here; allow auto-reconnect
    });

    this.socket.on('connect_error', async (err) => {
      console.log('Socket connect_error:', err);
      // Ensure future manual connect attempts are allowed
      this.connecting = false;
      const statusCode = err.data?.status_code;
      const message = err.data?.message;
      const messageCode = err.data?.message_code;
      if (statusCode === 401) {
        this.lastAuthErrorAt = Date.now();
        try {
          await this._handleAuthError();
        } catch (e) {
          console.error('Token refresh failed after server error 401:', e);
          this._notifyErrorListeners({
            type: 'auth_error',
            message
          });
        }
      } else {
        this._notifyErrorListeners({
          type: 'server_error',
          message,
          status_code: statusCode,
          message_code: messageCode,
          ...(err && err.data ? err.data : {})
        });
      }
    });

    this.socket.on('error', async (error) => {
      console.log('Socket server error:', error);

      // Backend emits structured errors with status_code
      if (error.status_code === 401) {
        this.lastAuthErrorAt = Date.now();
        try {
          await this._handleAuthError();
        } catch (e) {
          console.error('Token refresh failed after server error 401:', e);
          this._notifyErrorListeners({
            type: 'auth_error',
            message: error.message || 'Authentication failed'
          });
        }
      } else {
        this._notifyErrorListeners({
          type: 'server_error',
          ...error
        });
      }
    });
  }

  _setupEventListeners() {
    if (!this.socket || !this.connected) return;

    // Set up all registered event listeners
    Object.keys(this.eventListeners).forEach(eventName => {
      // Remove any existing listeners for this event to prevent duplicates
      this.socket.off(eventName);

      // Register the event listener
      this.socket.on(eventName, (data) => {
        this._handleEvent(eventName, data);
      });
    });


  }

  _handleEvent(eventName, data) {
    const listeners = this.eventListeners[eventName];
    if (!listeners || listeners.length === 0) return;

    // Update the last event timestamp using updated_at (backend standard)
    if (data && data.updated_at) {
      this.lastEventDateTime = data.updated_at;
      // Also sync time offset using updated_at if available
      this.updateServerTime(data.updated_at);
    }

    // Try to sync with server_datetime if available (preferred for time sync)
    if (data && data.server_datetime) {
      this.updateServerTime(data.server_datetime);
    }

    // Optimize: Use requestAnimationFrame for event handling to prevent blocking
    // Use a snapshot of listeners to avoid race conditions with unsubscribe during rAF
    const snapshot = listeners.slice();
    requestAnimationFrame(() => {
      snapshot.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for ${eventName}:`, error);
        }
      });
    });
  }

  _emitSubscription(resource, resourceIds = null, fromDateTime = null) {
    if (!this.socket || !this.connected) return;

    const subscriptionData = {
      resource: resource,
      resource_ids: resourceIds,
    };

    if (fromDateTime) {
      subscriptionData.from_datetime = fromDateTime;
    }

    this.socket.emit('subscribe', subscriptionData);


  }

  _emitUnsubscription(resource, resourceIds = null) {
    if (!this.socket || !this.connected) return;

    const unsubscriptionData = {
      resource: resource,
      resource_ids: resourceIds
    };

    this.socket.emit('unsubscribe', unsubscriptionData);

  }

  _resubscribeAll() {
    // Don't attempt to resubscribe if we're not connected
    if (!this.socket || !this.connected) {

      return;
    }

    // Add from_datetime to all resubscriptions
    const fromDateTime = this.lastEventDateTime;

    // Resubscribe to all resources
    Object.keys(this.resourceCounters).forEach(resource => {
      const subscriptionData = {
        resource: resource,
        from_datetime: fromDateTime
      };
      this.socket.emit('subscribe', subscriptionData);

    });

    // Resubscribe to all resource IDs
    Object.keys(this.resourceIdCounters).forEach(resource => {
      const ids = Object.keys(this.resourceIdCounters[resource]);
      if (ids.length > 0) {
        const subscriptionData = {
          resource: resource,
          resource_ids: ids,
          from_datetime: fromDateTime
        };
        this.socket.emit('subscribe', subscriptionData);

      }
    });
  }

  _notifyConnectionListeners(state) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  _notifyErrorListeners(error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

}

// Create a singleton instance
const socketService = new SocketService();

// Make the socket service globally available for disconnection on logout
window.socketService = socketService;

export default socketService;
